<script setup lang="ts">
import { ActionIcon, SearchableSelect } from '@taler/ui'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { type StarterChartTemplate, useCreateLedger } from '@/app/create-ledger'
import { useSession } from '@/app/session'
import LedgerCreateDialog from '@/app/shell/LedgerCreateDialog.vue'

const { t } = useI18n()
const session = useSession()
const dialogOpen = ref(false)
const createLedger = useCreateLedger()

async function submitCreate(name: string, template: StarterChartTemplate): Promise<void> {
  const id = await createLedger.mutateAsync({ name, template }).catch(() => undefined)
  if (id === undefined) return
  session.selectLedger(id)
  dialogOpen.value = false
}

function openDialog(closePopover: () => void): void {
  closePopover()
  createLedger.reset()
  dialogOpen.value = true
}

const options = computed(() =>
  session.ledgers.value.map((ledger) => ({
    value: ledger.id,
    label: ledger.name,
    role: ledger.role,
    archivedAt: ledger.archivedAt,
  })),
)

// The ledger's first letter stands in for the whole switcher once the sidebar collapses to
// its icon rail; the accessible name still carries the full ledger name either way.
const initial = computed(() => session.activeLedger.value?.name.trim().charAt(0).toUpperCase() ?? '?')

function choose(value: string | readonly string[]): void {
  if (typeof value === 'string') session.selectLedger(value)
}
</script>

<template>
  <div>
    <SearchableSelect
      :model-value="session.activeLedger.value?.id"
      :options="options"
      :trigger-label="t('shell.noLedger')"
      :search-label="t('app.searchLedgers')"
      :empty-text="t('app.noLedgersFound')"
      :aria-label="`${t('app.activeLedger')}: ${session.activeLedger.value?.name ?? t('shell.noLedger')}`"
      fill
      @update:model-value="choose"
    >
      <template #trigger="{ label }">
        <span
          aria-hidden="true"
          class="flex h-6 w-6 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-[color:var(--color-accent)] font-[family-name:var(--font-display)] text-[length:var(--text-step-2)] text-[color:var(--color-action-text)]"
        >
          {{ initial }}
        </span>
        <span class="hidden min-w-0 flex-1 truncate text-left wide:block">{{ label }}</span>
      </template>
      <template #indicator>
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          aria-hidden="true"
          class="hidden shrink-0 text-[color:var(--color-text-muted)] wide:block"
        >
          <path
            d="M5 6 8 3l3 3M5 10l3 3 3-3"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </template>
      <template #item="{ option }">
        <span
          class="min-w-0 flex-1 truncate text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
          :class="option.value === session.activeLedger.value?.id ? 'font-semibold' : 'font-medium'"
        >
          {{ option.label }}
        </span>
        <span
          class="shrink-0 whitespace-nowrap rounded-full border border-[color:var(--color-border)] px-[var(--space-inline)] text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
        >
          {{ option.role }}
        </span>
        <span
          v-if="option.archivedAt"
          class="shrink-0 whitespace-nowrap text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-attention)]"
        >
          {{ t('app.archived') }}
        </span>
      </template>
      <template #footer="{ close }">
        <button
          type="button"
          class="flex min-h-[var(--density-target-min)] w-full items-center gap-[var(--space-cell)] rounded-[var(--radius-control)] px-[var(--space-cell)] text-left text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)] hover:bg-[color:var(--color-surface-raised)]"
          @click="openDialog(close)"
        >
          <ActionIcon name="plus" />
          {{ t('shell.createLedger.action') }}
        </button>
      </template>
    </SearchableSelect>
    <LedgerCreateDialog
      :open="dialogOpen"
      :is-submitting="createLedger.isPending.value"
      :has-error="createLedger.isError.value"
      @submit="submitCreate"
      @close="dialogOpen = false"
    />
  </div>
</template>
